import type { LayerSpecification, Map as MlMap, SourceSpecification, StyleSpecification, VectorTileSource } from "maplibre-gl";
import { addProtocol, setWorkerUrl } from "maplibre-gl";
import { Protocol } from "pmtiles";

let registered = false;
const BASEMAP = "protomaps";

/** basemap source and layers of the style, kept to put the basemap back when it was left out */
let base: { source: Record<string, unknown>; layers: LayerSpecification[] } | null = null;

const basemapSource = (basemapUrl: string) => `pmtiles://${window.location.origin}${basemapUrl}`;

/**
 * Protomaps style of the Android prototype, basemap read by range requests from /packs (SW §3.2).
 * [basemapUrl]: basemap of a regional pack (one per department). Without it the style has
 * no basemap at all (background only): there is no default basemap file since the departmental packs
 * it is added later by [applyBasemap] once the pack is known.
 */
export async function loadBaseStyle(basemapUrl?: string | null): Promise<StyleSpecification> {
  if (!registered) {
    setWorkerUrl("/maplibre/maplibre-gl-worker.mjs");
    addProtocol("pmtiles", new Protocol().tile);
    registered = true;
  }
  const text = await (await fetch("/style/style.json")).text();
  const style = JSON.parse(text.split("{origin}").join(window.location.origin)) as StyleSpecification;
  const isBase = (l: LayerSpecification) => "source" in l && l.source === BASEMAP;
  base = { source: { ...(style.sources[BASEMAP] as object) }, layers: style.layers.filter(isBase) };
  if (basemapUrl) (style.sources[BASEMAP] as { url?: string }).url = basemapSource(basemapUrl);
  else {
    delete style.sources[BASEMAP];
    style.layers = style.layers.filter((l) => !isBase(l));
  }
  return style;
}

/**
 * Shows the basemap [basemapUrl] (null: none) on a loaded map, keeping the layers added above it.
 * Used when the pack becomes known after the map was created, or changes with the view (SW-DP-04).
 */
export function applyBasemap(m: MlMap, basemapUrl: string | null) {
  if (!base) return;
  const cur = m.getSource(BASEMAP) as VectorTileSource | undefined;
  if (!basemapUrl) {
    if (!cur) return;
    for (const l of base.layers) if (m.getLayer(l.id)) m.removeLayer(l.id);
    m.removeSource(BASEMAP);
    return;
  }
  const url = basemapSource(basemapUrl);
  if (cur) { if (cur.url !== url) cur.setUrl(url); return; }
  m.addSource(BASEMAP, { ...base.source, url } as SourceSpecification);
  // right above the background, under everything the console draws
  const before = m.getStyle().layers.find((l) => l.type !== "background")?.id;
  for (const l of base.layers) m.addLayer(l, before);
}

/** Arrow pictogram drawn on a canvas, used along oneway lifted / reversed stretches and courses. */
export function arrowImage(color: string, double: boolean): ImageData {
  const w = 24, h = 16;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const g = c.getContext("2d")!;
  g.fillStyle = color;
  g.strokeStyle = "#ffffff";
  g.lineWidth = 2;
  const head = (x: number, dir: 1 | -1) => {
    g.beginPath();
    g.moveTo(x, h / 2);
    g.lineTo(x - dir * 8, 2);
    g.lineTo(x - dir * 8, h - 2);
    g.closePath();
    g.stroke();
    g.fill();
  };
  head(w - 3, 1);
  if (double) head(3, -1);
  return g.getImageData(0, 0, w, h);
}
